import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";
import useTestMutation from "../../api/mutations/testMutation";
import { useGetPrices } from "../../api/queries/test.queries";
import circle from "../../assets/enter-code-circle.svg";
import { CustomButton } from "../../components/CustomButton";
import GoogleTag from "../../components/GoogleTag";
import warning from "../../assets/warning.svg";

const TEXT = {
  HEADER: "Vad roligt att du är här!",
  DESCRIPTION:
    "Vi ser att du använder Facebook/Instagram. För att Swishbetalningen ska fungera korrekt, vänligen öppna denna sida i din vanliga webbläsare - Safari eller Chrome. Du kan fortsatt använda kort eller Swish på annan enhet.",
  PLACEHOLDER: "Namn",
  PRICE_LABEL: "Pris:",
  PRICE_VALUE: "kr",
  BUTTON_TEXT: "Betala test",
  ERROR_MESSAGE: "Namn är obligatoriskt",
  GENERATE_TEST_ERROR: "Kunde inte starta testet.",
  BUY_TEST_ERROR: "Kunde inte köpa testet.",
};

interface FormValues {
  name: string;
}

const schema = yup.object().shape({
  name: yup.string().required(TEXT.ERROR_MESSAGE),
});

const CheckOutPage: React.FC = () => {
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const isFacebook = ua.includes("fbav");
    const isInstagram = ua.includes("instagram");
    setIsInAppBrowser(isFacebook || isInstagram);
  }, []);

  const testName = "EQsparande";
  // const testName = "Test 2";

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [price, setPrice] = useState<number | null | undefined>(null);
  const { generateTest, buyTest } = useTestMutation();
  const { data: prices } = useGetPrices();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    // @ts-ignore
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    const { name } = data;

    try {
      // Step 1: Generate the test
      const generateResponse = await generateTest.trigger({
        testName,
        userName: name,
      });
      if (generateResponse.status === 200) {
        const isn = generateResponse.data?.data?.value;
        // Step 2: Buy the test
        const buyResponse = await buyTest.trigger({ isn });
        if (buyResponse.status === 200) {
          // Store the isn code in localStorage
          localStorage.setItem("isn", isn);
          localStorage.setItem("userName", name);
          const paymentUrl = buyResponse.data?.data?.value;
          window.location.href = paymentUrl;
        } else {
          throw new Error(TEXT.BUY_TEST_ERROR);
        }
      } else {
        throw new Error(TEXT.GENERATE_TEST_ERROR);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error.message ||
        TEXT.GENERATE_TEST_ERROR;
      toast.error(errorMessage, {
        style: {
          backgroundColor: "#3F51B5",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (prices) {
      const testPrice = prices?.data.find(
        (test) => test.name === testName
      )?.price;

      setPrice(testPrice);
    }
  }, [prices]);

  return (
    <main className="flex-grow h-full flex items-center justify-center">
      <GoogleTag />
      <div className="flex flex-col justify-center items-center w-full">
        <div className="w-full h-full bg-primary-card rounded-lg p-8 text-start max-w-[744px] mx-3 md:px-10 font-nunito flex flex-col gap-9">
          <h2 className="faq-header text-white text-2xl lg:text-4xl font-semibold">
            {TEXT.HEADER}
          </h2>
          {isInAppBrowser && (
            <div className="flex flex-row gap-7 items-center justify-center">
              <img
                src={warning}
                alt="warning"
                className={`w-[40px] h-[40px]`}
              />
              <p className="faq-answer text-lg text-white bg-primary-card font-quicksand">
                {TEXT.DESCRIPTION}
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <input
              type="text"
              placeholder={TEXT.PLACEHOLDER}
              {...register("name")}
              className={`flex p-2 md:p-4 items-center gap-2 self-stretch rounded-lg 
              border-2 border-[#3F51B5] bg-[#1C2434] text-white
              placeholder:text-[#3F51B5] focus:ring-2 focus:ring-[#3F51B5] outline-none
              w-full`}
            />
            {errors.name && (
              <p className="text-red-500 text-base font-bold mt-1">
                {errors.name.message}
              </p>
            )}
            <div className="flex justify-between mt-4">
              <p className="faq-answer text-lg text-white bg-primary-card">
                {TEXT.PRICE_LABEL}
              </p>
              <p className="faq-answer text-lg text-white bg-primary-card">
                {price || "-"} {TEXT.PRICE_VALUE}
              </p>
            </div>

            <div className="flex w-full justify-center items-center mt-6">
              <CustomButton
                customStyles={"h-[56px] w-full md:max-w-[330px]"}
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Laddar..." : TEXT.BUTTON_TEXT}
              </CustomButton>
            </div>
          </form>
        </div>

        <img
          src={circle}
          alt="main-photo"
          className="absolute bottom-16 right-0 w-[230px] h-[230px] md:w-[300px] md:h-[300px] -z-10"
        />
      </div>
    </main>
  );
};

export default CheckOutPage;
